import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import { LoadToCosmos, SendToCosmos } from './SendToCosmos';

const SettingsPrompt = () => {
  const [loading, setLoading] = useState(true);
  const [sectionList, setSectionList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isNewMode, setIsNewMode] = useState(false);
  const [newCompId, setNewCompId] = useState("");
  const [newCompName, setNewCompName] = useState("");

  useEffect(() => {
    fetchFileListbu();
  }, []);
  const [draft, setDraft] = useState({
    data: "",
    prompt: "",
    faqapi: "",
    webhook: ""
  });
  useEffect(() => {
    if (selectedSection) {
      setDraft({
        data: JSON.stringify(selectedSection.data, null, 2),
        prompt: JSON.stringify(selectedSection.prompt, null, 2),
        faqapi: JSON.stringify(selectedSection.faqapi, null, 2),
        webhook: JSON.stringify(selectedSection.webhook, null, 2),
      });
    }
  }, [selectedSection]);

  const fetchFileListbu = async () => {
    try {
      const uppayload = {
        id: "all",
        container: "con-concierge-prompt",
      };

      const items = await LoadToCosmos(uppayload);
      const sections = items.item.map((section) => ({
        id: section.id,
        name: section.name,
        data: section.data || "",
        prompt: section.messages || "",
        faqapi: section.faqapi || "",
        webhook: section.webhook || "",
      }));

      setSectionList(sections);
    } catch (error) {
      console.error("データ取得エラー:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedSection(null);
    setIsNewMode(false);
    setNewCompId("");
    setNewCompName("");
  };

  const handleAddNew = async () => {
    try {
      // 999999のプロンプトを取得
      const baseSection = sectionList.find(section => section.id === "999999");
      
      if (!baseSection) {
        alert("ベースとなるプロンプト（999999）が見つかりません");
        return;
      }

      setSelectedSection({
        id: "",
        name: "",
        data: baseSection.data,
        prompt: baseSection.prompt,
        faqapi: baseSection.faqapi,
        webhook: baseSection.webhook,
      });
      setIsNewMode(true);
      setOpenModal(true);
    } catch (error) {
      console.error("新規作成エラー:", error);
    }
  };

  const handleSave = async () => {
    if (!selectedSection) return;

    // 新規作成モードの場合、入力チェック
    if (isNewMode) {
      if (!newCompId.trim() || !newCompName.trim()) {
        alert("顧客IDと顧客名を入力してください");
        return;
      }
      
      // 既存IDとの重複チェック
      if (sectionList.some(section => section.id === newCompId.trim())) {
        alert("この顧客IDは既に存在します");
        return;
      }
    }

    const payload = {
      id: isNewMode ? newCompId.trim() : selectedSection.id,
      name: isNewMode ? newCompName.trim() : selectedSection.name,
      data: selectedSection.data,
      messages: selectedSection.prompt,
      faqapi: selectedSection.faqapi,
      webhook: selectedSection.webhook,
      container: "con-concierge-prompt",
    };

    try {
      await SendToCosmos(payload);
      handleCloseModal();
      fetchFileListbu(); // 再読み込み
    } catch (error) {
      console.error("保存エラー:", error);
    }
  };

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: 16, mb: 0 }}>
          プロンプト一覧
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddNew}
          sx={{ minWidth: 'auto', px: 2 }}
        >
          ＋
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <Box sx={{ maxHeight: 'calc(100vh - 180px)', overflow: 'auto' }}>
          <List dense> {/* ← denseで全体コンパクト化 */}
            {sectionList.map((section, index) => (
              <React.Fragment key={section.id}>
                <ListItem
                  dense
                  button
                  onClick={() => {
                    setSelectedSection(section);
                    setOpenModal(true);
                  }}
                  sx={{
                    cursor: 'pointer',
                    py: 0.5,            // ← 縦幅縮める
                  }}
                >
                  <ListItemText
                    primary={`${section.id} ${section.name}`}
                    primaryTypographyProps={{ fontSize: 13 }}  // ← 文字小さく
                  />
                </ListItem>

                {/* 行ごとの仕切り線 */}
                {index < sectionList.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}

      {/* --- モーダル（編集/新規） --- */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="false" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>
          {isNewMode ? "プロンプト新規作成" : "プロンプト編集"}
        </DialogTitle>

        <DialogContent dividers>
          {selectedSection && (
            <>
              {isNewMode ? (
                <>
                  <TextField
                    fullWidth
                    margin="dense"
                    label="顧客ID"
                    value={newCompId}
                    onChange={(e) => setNewCompId(e.target.value)}
                    size="small"
                    placeholder="顧客IDを入力"
                    required
                  />
                  <TextField
                    fullWidth
                    margin="dense"
                    label="顧客名"
                    value={newCompName}
                    onChange={(e) => setNewCompName(e.target.value)}
                    size="small"
                    placeholder="顧客名を入力"
                    required
                  />
                </>
              ) : (
                <>
                  <TextField
                    fullWidth
                    margin="dense"
                    label="顧客ID"
                    value={selectedSection.id}
                    disabled
                    size="small"
                  />
                  <TextField
                    fullWidth
                    margin="dense"
                    label="顧客名"
                    value={selectedSection.name}
                    disabled
                    size="small"
                  />
                </>
              )}
              <TextField
                fullWidth
                margin="dense"
                label="チャットメッセージ"
                multiline
                rows={10}
                size="small"
                value={draft.data}
                onChange={(e) => setDraft((prev) => ({ ...prev, data: e.target.value }))}
              />
              <TextField
                fullWidth
                margin="dense"
                label="プロンプト"
                multiline
                rows={10}
                size="small"
                value={draft.prompt}
                onChange={(e) => setDraft((prev) => ({ ...prev, prompt: e.target.value }))}
              />
              <TextField
                fullWidth
                margin="dense"
                label="FAQ API"
                multiline
                rows={5}
                size="small"
                value={draft.faqapi}
                onChange={(e) => setDraft((prev) => ({ ...prev, faqapi: e.target.value }))}
              />
              <TextField
                fullWidth
                margin="dense"
                label="Webhook"
                multiline
                rows={10}
                size="small"
                value={draft.webhook}
                onChange={(e) => setDraft((prev) => ({ ...prev, webhook: e.target.value }))}
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal}>閉じる</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              try {
                setSelectedSection({
                  ...selectedSection,
                  data: JSON.parse(draft.data),
                  prompt: JSON.parse(draft.prompt),
                  faqapi: JSON.parse(draft.faqapi),
                  webhook: JSON.parse(draft.webhook),
                });
                setConfirmOpen(true);
              } catch {
                alert("JSON形式が正しくありません");
              }
            }}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 保存確認 */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={{ fontSize: 16 }}>保存確認</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>保存しますか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>いいえ</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              await handleSave();
              setConfirmOpen(false);
              setSnackbarOpen(true);
            }}
          >
            はい
          </Button>
        </DialogActions>
      </Dialog>

      {/* 保存完了メッセージ */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          保存しました。
        </Alert>
      </Snackbar>
    </Box>

  );
};

export default SettingsPrompt;
